// src/data/compPlans.js
// Seeded from SYNERGY_COMP__1_.xlsx - SYNERGY 1.31.26 plan
// Add additional plans via the Admin > Comp Plans UI

export const CARRIERS = ['Spectrum', 'Frontier', 'Xfinity'];

export const ROLES = ['Rep', 'Lead', 'Manager', 'Regional', 'Asst Recruiter', 'Recruiter', 'Director'];

export const ROLE_KEYS = ['Rep', 'Lead', 'Manager', 'Regional', 'AsstRecruiter', 'Recruiter', 'Director'];

export const ROLE_LABELS = {
  Rep: 'Rep',
  Lead: 'Lead',
  Manager: 'Manager',
  Regional: 'Regional',
  AsstRecruiter: 'Asst Recruiter',
  Recruiter: 'Recruiter',
  Director: 'Director',
};

// ISO plan type — all commission goes to the ISO entity, not individual reps
export const PLAN_TYPES = ['Standard', 'ISO'];

export const SPECTRUM_PRODUCTS = [
  { id: 'gig', name: 'Gig', carrier: 'Spectrum' },
  { id: 'internet_premier', name: 'Internet (Premier)', carrier: 'Spectrum' },
  { id: 'internet_assist', name: '50/100 Internet Assist (Advantage)', carrier: 'Spectrum' },
  { id: 'tv_select', name: 'TV Select / Mi Plan Latino', carrier: 'Spectrum' },
  { id: 'tv_choice', name: 'TV Choice / Essentials / Stream / Limited Basic', carrier: 'Spectrum' },
  { id: 'mobile_line1', name: 'Mobile Line 1', carrier: 'Spectrum' },
  { id: 'mobile_lines2to10', name: 'Mobile Lines 2–10', carrier: 'Spectrum' },
  { id: 'mobile_line1_ild', name: 'Mobile Line 1 ILD+', carrier: 'Spectrum' },
  { id: 'mobile_lines2to10_ild', name: 'Mobile Lines 2+ ILD+', carrier: 'Spectrum' },
  { id: 'uld_plus', name: 'ULD+', carrier: 'Spectrum' },
];

// Default comp plan seeded from SYNERGY_COMP__1_.xlsx
export const DEFAULT_COMP_PLAN = {
  id: 'synergy_1_31_26',
  name: 'Synergy 1.31.26',
  carrier: 'Spectrum',
  planType: 'Standard',
  rates: {
    gig:                   { Rep: 325, Lead: 0, Manager: 0, Regional: 100, AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    internet_premier:      { Rep: 275, Lead: 0, Manager: 0, Regional: 85,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    internet_assist:       { Rep: 210, Lead: 0, Manager: 0, Regional: 50,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    tv_select:             { Rep: 50,  Lead: 0, Manager: 0, Regional: 20,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    tv_choice:             { Rep: 20,  Lead: 0, Manager: 0, Regional: 10,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    mobile_line1:          { Rep: 70,  Lead: 0, Manager: 0, Regional: 20,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    mobile_lines2to10:     { Rep: 110, Lead: 0, Manager: 0, Regional: 40,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    mobile_line1_ild:      { Rep: 70,  Lead: 0, Manager: 0, Regional: 20,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    mobile_lines2to10_ild: { Rep: 110, Lead: 0, Manager: 0, Regional: 40,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
    uld_plus:              { Rep: 25,  Lead: 0, Manager: 0, Regional: 20,  AsstRecruiter: 0, Recruiter: 0, Director: 0 },
  }
};

export function calculateCommission(deal, reps, compPlans) {
  // Non-payable orders (secondary/room accounts) get $0 commission
  if (deal.nonPayable) return [];

  // Determine comp plan: use order's compPlanId, or fall back to primary rep's plan
  let compPlanId = deal.compPlanId;
  if (!compPlanId && deal.repId) {
    const primaryRep = reps.find(r => r.id === deal.repId);
    if (primaryRep) compPlanId = primaryRep.compPlanId || 'synergy_1_31_26';
  }
  if (!compPlanId && deal.overrides?.length > 0) {
    const firstRep = reps.find(r => r.id === deal.overrides[0].repId);
    if (firstRep) compPlanId = firstRep.compPlanId || 'synergy_1_31_26';
  }
  compPlanId = compPlanId || 'synergy_1_31_26';

  const plan = compPlans.find(p => p.id === compPlanId) || DEFAULT_COMP_PLAN;
  const rates = plan.rates[deal.productId] || {};
  const qty = deal.quantity || 1;

  // ISO plan: all commission goes to the ISO entity (first override only)
  if (plan.planType === 'ISO' && deal.overrides?.length > 0) {
    const isoOverride = deal.overrides[0];
    const rep = reps.find(r => r.id === isoOverride.repId);
    const totalRate = Object.values(rates).reduce((a, v) => a + (Number(v) || 0), 0);
    return [{
      repId: isoOverride.repId,
      repName: rep ? rep.name : 'ISO',
      role: 'ISO',
      amount: totalRate * qty,
    }].filter(e => e.amount > 0);
  }

  // Start with explicit overrides from the order
  let overrides = deal.overrides || [];

  // If no overrides but order has a repId, create an implicit override for the primary rep
  if (overrides.length === 0 && deal.repId) {
    const primaryRep = reps.find(r => r.id === deal.repId);
    if (primaryRep) {
      overrides = [{ repId: primaryRep.id, role: primaryRep.role || 'Rep' }];
    }
  }

  const results = overrides.map(o => {
    const rep = reps.find(r => r.id === o.repId);
    const roleKey = (o.role || 'Rep').replace(/ /g, '');
    const amount = (rates[roleKey] || 0) * qty;
    return {
      repId: o.repId,
      repName: rep ? rep.name : 'Unknown',
      role: o.role || 'Rep',
      amount,
    };
  });

  // Auto-include the agent's named uplines (Lead/Manager/Director/Regional/
  // Recruiter/AsstRecruiter) at the rates of the AGENT's comp plan. This is
  // how recruiters who recruit across teams, and directors who manage agents
  // on multiple comp plans, get the right override.
  const overrideRepIds = new Set(overrides.map(o => o.repId));
  let primaryRep = deal.repId ? reps.find(r => r.id === deal.repId) : null;
  if (!primaryRep && overrides.length > 0) {
    primaryRep = reps.find(r => r.id === overrides[0].repId);
  }
  if (primaryRep?.uplines) {
    Object.entries(primaryRep.uplines).forEach(([roleKey, upline]) => {
      if (!upline?.id) return;
      const upRep = reps.find(r => r.id === upline.id || r.uid === upline.id);
      if (!upRep) return;
      if (overrideRepIds.has(upRep.id)) return;
      if (results.some(r => r.repId === upRep.id && r.role === (ROLE_LABELS[roleKey] || roleKey))) return;
      if (!upRep.active && upRep.active !== undefined) return;
      if (upRep.role === 'admin') return;
      const amount = (rates[roleKey] || 0) * qty;
      if (amount > 0) {
        results.push({
          repId: upRep.id,
          repName: upRep.name || upline.name || 'Unknown',
          role: ROLE_LABELS[roleKey] || roleKey,
          amount,
        });
      }
    });
  }

  return results.filter(e => e.amount > 0);
}
